import { useState } from 'react'
import { motion } from 'framer-motion'

const overlayStats = [
  { value: '100+', label: 'Clients' },
  { value: '11', label: 'Years' },
  { value: '97%+', label: 'Retention' },
  { value: '24/7', label: 'Monitoring' },
]

export function FounderSection() {
  const [imgError, setImgError] = useState(false)
  return (
    <section style={{ padding: '120px 0', background: 'var(--bg)' }}>
      <div className="max-w-6xl mx-auto px-6">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 80,
            alignItems: 'center',
          }}
          className="grid-cols-1 lg:grid-cols-2"
        >
          <motion.div
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <div
              className="founder-photo relative overflow-hidden"
              style={{
                aspectRatio: '4/5',
                border: '1px solid rgba(78,125,233,0.18)',
              }}
            >
              {imgError ? (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{ background: 'var(--bg-3)', color: 'var(--white-dim)' }}
                >
                  Photo
                </div>
              ) : (
                <picture>
                  <source srcSet="/josiah.webp" type="image/webp" />
                  <img
                    src="/josiah.jpg"
                    alt="Josiah Philipsen — Founder, NextTech Consultants"
                    width={600}
                    height={800}
                    loading="eager"
                    fetchpriority="high"
                    className="w-full h-full object-cover"
                    style={{ width: '100%', height: 'auto' }}
                    onError={() => setImgError(true)}
                  />
                </picture>
              )}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                  zIndex: 2,
                }}
              >
                {overlayStats.map((s) => (
                  <div
                    key={s.label}
                    style={{
                      background: 'rgba(14,18,32,0.88)',
                      backdropFilter: 'blur(12px)',
                      borderTop: '1px solid rgba(78,125,233,0.2)',
                      padding: '14px 18px',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'Barlow Condensed, sans-serif',
                        fontSize: 28,
                        fontWeight: 900,
                        color: 'var(--accent)',
                        lineHeight: 1,
                      }}
                    >
                      {s.value}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--white-dim)',
                        marginTop: 3,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                      }}
                    >
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={false}
            animate={{ opacity: 1, x: 0 }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'var(--accent-glow)',
                border: '1px solid var(--accent-line)',
                padding: '8px 16px',
                borderRadius: 2,
                marginBottom: 28,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'var(--accent)',
                }}
              >
                A Message From Our Founder
              </span>
            </div>
            <h2
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(34px, 4vw, 50px)',
                textTransform: 'uppercase',
                lineHeight: 1.05,
                marginBottom: 28,
                color: 'var(--white)',
              }}
            >
              We built NTC because business owners deserve better than <span style={{ color: 'var(--accent)', fontStyle: 'normal' }}>break-fix</span> support.
            </h2>
            <div style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--white-dim)', marginBottom: 18 }}>
              <p className="mb-4">
                I started NTC because I was a business owner first. I know what it's like to lose half a day to a tech issue that shouldn't have happened. I know the stress of not knowing if your data is safe, or if your team has the right tools to compete.
              </p>
              <p className="mb-4">
                When you work with NTC, you get a team of six people who treat your business like it's our own. We answer the phone. We fix it before you notice it's broken. And we're always thinking about where technology is headed — so you're never caught behind.
              </p>
              <p>
                We started building with AI years ago, back when it meant writing scripts. When large language models changed the industry, we didn't wait and see — we went deep. But our biggest concern has always been security. AI that can access everything is AI that can share the wrong thing. Every tool we build and every tool we roll out for clients starts with the right guardrails in place.
              </p>
            </div>
            <div
              style={{
                marginTop: 32,
                paddingTop: 28,
                borderTop: '1px solid var(--white-faint)',
              }}
            >
              <div
                style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 22,
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--white)',
                }}
              >
                Josiah Philipsen
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--accent)',
                  marginTop: 4,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                Founder & Owner, NextTech Consultants
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        .founder-photo::before {
          content: "";
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 3px;
          background: var(--accent);
          z-index: 2;
        }
        @media (max-width: 1024px) {
          .grid-cols-1.lg\\:grid-cols-2 { grid-template-columns: 1fr !important; }
        }
      ` }} />
    </section>
  )
}
